import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, Input, Label } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useForm } from "react-hook-form";
import { Pencil, AlertCircle } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { updateDoctor, viewDoctor } from "./service/doctor-service";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Controller } from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import S3ImageLoader from "../../S3ImageLoader";
import { getFileUrl } from "../../utils/getFileUrl";

const SignaturePreview = ({ src, alt }) => {
    if (!src) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ width: "100%", height: "160px" }}
            >
                <span className="text-muted">No Signature Preview</span>
            </div>
        );
    }

    if (src.startsWith("data:") || src.startsWith("blob:") || src.startsWith("http")) {
        return (
            <img
                src={src}
                alt={alt}
                style={{ width: "70%", height: "100px", objectFit: "contain" }}
            />
        );
    }

    return (
        <S3ImageLoader
            fileName={src}
            fallbackSrc=""
            alt={alt}
            style={{ width: "70%", height: "100px", objectFit: "contain" }}
        />
    );
};

export default function EditDoctor() {

    const { id } = useParams()
    const location = useLocation()
    const isClinicDoctor =
        location.state?.isClinicDoctor === true ||
        new URLSearchParams(location.search).get("isClinicDoctor") === "true"
    const [profileImage, setProfileImage] = useState(null);
    const [imageError, setImageError] = useState("");        // This will now hold the preview (Base64)
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [signatureMode, setSignatureMode] = useState("draw");
    const [signatureName, setSignatureName] = useState("");
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const signRef = useRef(null);
    const signatureFileRef = useRef(null);
    const { setLoading } = useLoader();
    const navigate = useNavigate()

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting },
        control
    } = useForm();
    const reportDegree = watch("degree");

    useEffect(() => {
        getData()
    }, [])

    const getListDoctor = () => {
        if (location.state?.doctor) return location.state.doctor;
        if (!isClinicDoctor) return null;
        try {
            const stored = sessionStorage.getItem(`clinic-doctor-${id}`);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            return null;
        }
    };

    const pickValue = (...values) => {
        for (const value of values) {
            if (value !== null && value !== undefined && value !== "") return value;
        }
        return "";
    };

    const applyDoctor = async (doc = {}, listDoc = {}) => {
        reset({
            firstName: pickValue(doc.fname, listDoc.fname),
            lastName: pickValue(doc.lname, listDoc.lname),
            email: pickValue(doc.email, listDoc.email),
            mobile: pickValue(doc.mobileNumber, listDoc.mobileNumber),
            degree: pickValue(doc.degree, doc.signatureQualification, listDoc.degree),
            hospital: pickValue(doc.hospitalName, listDoc.hospitalName),
            commision: pickValue(doc.commissionPercent, listDoc.commissionPercent),
            address: pickValue(doc.address, listDoc.address),
        });
        const profileKey = pickValue(doc.profileImage, doc.profilePicture, listDoc.profileImage);
        if (profileKey && !String(profileKey).startsWith("http") && !String(profileKey).startsWith("data:")) {
            const profileUrl = await getFileUrl(profileKey);
            setProfileImage(profileUrl || "");
        } else {
            setProfileImage(profileKey || null);
        }
        setSignatureName(pickValue(doc.signatureName, listDoc.signatureName));
        const existingSignature = pickValue(doc.signature, doc.signatureImage, listDoc.signature);
        if (existingSignature) {
            setSignaturePreview(existingSignature);
        }
    };

    const getData = async () => {
        setLoading(true)
        const listDoc = isClinicDoctor ? (getListDoctor() || {}) : {};
        try {
            const res = await viewDoctor(id, isClinicDoctor ? { isClinicDoctor: true } : {});
            if (res.data.success == true) {
                await applyDoctor(res.data.data || {}, listDoc);
            } else if (isClinicDoctor) {
                await applyDoctor({}, listDoc);
            }

        } catch (error) {
            console.log(error)
            if (isClinicDoctor) {
                await applyDoctor({}, listDoc);
            }
        } finally {
            setLoading(false)
        }
    }

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // File size check
        if (file.size > 5 * 1024 * 1024) {
            setImageError("File size must be less than 5MB");
            return;
        }

        // File type check
        if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
            setImageError("Only JPG, PNG, or GIF files are allowed");
            return;
        }

        setImageError("");

        // Store the actual File object
        setProfileImageFile(file);

        // Create preview (Base64) - only for UI
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfileImage(reader.result); // Base64 string for preview
        };
        reader.readAsDataURL(file);
    };

    const clearSignature = () => {
        if (signRef.current) signRef.current.clear();
        setSignaturePreview(null);
        setSignatureFile(null);
        if (signatureFileRef.current) signatureFileRef.current.value = "";
    };

    const updateCanvasPreview = () => {
        if (!signRef.current || signRef.current.isEmpty()) {
            setSignaturePreview(null);
            return;
        }
        setSignaturePreview(signRef.current.toDataURL("image/png"));
    };

    const handleSignatureMode = (mode) => {
        setSignatureMode(mode);
        clearSignature();
    };

    const handleSignatureUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.warning("Signature file size must be less than 5MB");
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast.warning("Only image files are allowed for signature");
            return;
        }

        setSignatureFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setSignaturePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const getSignatureFile = async () => {
        if (signatureMode === "draw") {
            if (!signRef.current || signRef.current.isEmpty()) return null;
            const dataURL = signRef.current.toDataURL("image/png");
            const blob = await (await fetch(dataURL)).blob();
            return new File([blob], "signature.png", { type: "image/png" });
        }
        return signatureFile;
    };

    const onSubmit = async (data) => {
        setLoading(true)
        try {
            const form_data = new FormData();
            form_data.append("fname", data.firstName);
            form_data.append("lname", data.lastName);
            form_data.append("email", data.email);
            form_data.append("mobileNumber", data.mobile);
            form_data.append("degree", data.degree);
            form_data.append("hospitalName", data.hospital);
            form_data.append("commissionPercent", data.commision);
            form_data.append("address", data.address);
            if (profileImageFile) {
                form_data.append("profilePicture", profileImageFile);
            }
            form_data.append("signatureName", signatureName || "");
            const signature = await getSignatureFile();
            if (signature) {
                form_data.append("signature", signature);
            }
            if (isClinicDoctor) {
                form_data.append("isClinicDoctor", true);
            }
            const res = await updateDoctor(id, form_data);
            if (res.data.success == true) {
                toast.success(res.data.message);
                navigate(`${process.env.PUBLIC_URL}/doctors-list`)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
        // console.log("Form submitted:", { ...data, profileImage });
        // alert("Doctor added successfully!");
        reset();
        setProfileImage(null);
    };

    return (
        <>
            <Breadcrumbs mainTitle="Update Doctor’s Details" parent="Home" subParent="Doctor List" title="Update Doctor’s Details" />

            <Container fluid={true}>
                <Row className="tw-flex tw-justify-center">
                    <Col md="12" lg="12">
                        <Card className="tw-shadow-xl tw-rounded-2xl tw-border-0 tw-bg-white tw-p-8">
                            <div className="row">
                                <div className="col-md-4">
                                    {/* Profile Image */}
                                    <div className="tw-flex tw-flex-col tw-items-center tw-mb-8">
                                        <div className="tw-relative">
                                            <label htmlFor="profile-upload" className="tw-cursor-pointer">
                                                <div className="tw-w-32 tw-h-32 tw-rounded-full tw-overflow-hidden tw-bg-gray-100 tw-border tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-ring-2 hover:bg-primary tw-transition-all">
                                                    {profileImage ? (
                                                        <img
                                                            src={profileImage}
                                                            alt="Profile"
                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                        />
                                                    ) : (
                                                        <span className="tw-text-gray-400 tw-text-sm">Upload Image</span>
                                                    )}
                                                </div>
                                            </label>
                                            <label
                                                htmlFor="profile-upload"
                                                className="tw-absolute tw-bottom-2 tw-right-2 bg-primary tw-rounded-full tw-p-2 tw-shadow-md tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-border-2 tw-border-white"
                                            >
                                                <Pencil className="tw-w-4 tw-h-4 tw-text-white" />
                                            </label>
                                            <input
                                                id="profile-upload"
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png,image/gif"
                                                onChange={handleImageUpload}
                                                className="tw-hidden"
                                            />
                                        </div>
                                        {imageError && (
                                            <div className="tw-flex tw-items-center tw-gap-1 tw-text-red-600 tw-text-sm tw-mt-2">
                                                <AlertCircle className="tw-w-4 tw-h-4" />
                                                <span>{imageError}</span>
                                            </div>
                                        )}
                                        <p className="tw-text-gray-500 tw-text-xs tw-mt-1">
                                            {/* JPG, PNG or GIF. Max 5MB. (Optional) */}
                                            Profile Image
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-8">
                                    {/* Form */}
                                    <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-5">
                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* First Name */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    First Name <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter first name"
                                                    {...register("firstName", { required: "First name is required" })}
                                                />
                                                {errors.firstName && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.firstName.message}</p>
                                                )}
                                            </div>

                                            {/* Last Name */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Last Name <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter last name"
                                                    {...register("lastName", { required: "Last name is required" })}
                                                />
                                                {errors.lastName && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.lastName.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* Email */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Email <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="Enter email address"
                                                    {...register("email", {
                                                        required: "Email is required",
                                                        pattern: {
                                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                            message: "Enter a valid email address",
                                                        },
                                                    })}
                                                />
                                                {errors.email && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email.message}</p>
                                                )}
                                            </div>

                                            {/* Mobile Number*/}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Mobile Number <span className="tw-text-red-500">*</span>
                                                </label>
                                                <Controller
                                                    name="mobile"
                                                    control={control}
                                                    rules={{
                                                        required: "Mobile is required",
                                                        minLength: {
                                                            value: 10,
                                                            message: "Invalid mobile number",
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <PhoneInput
                                                            {...field}
                                                            country="in"
                                                            enableSearch
                                                            placeholder="+91 9876543210"
                                                            inputClass="form-control"
                                                            inputStyle={{ width: "100%" }}
                                                            onChange={(value) => field.onChange(value)}
                                                        />
                                                    )}
                                                />
                                                {/* <input
                                                    type="tel"
                                                    maxLength="10"
                                                    className="form-control"
                                                    placeholder="Enter mobile number"
                                                    {...register("mobile", {
                                                        required: "Mobile number is required",
                                                        pattern: {
                                                            value: /^[0-9]{10}$/,
                                                            message: "Enter a valid 10-digit number",
                                                        },
                                                    })}
                                                /> */}
                                                {errors.mobile && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.mobile.message}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* Degree (optional) */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">Degree (Optional)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter degree"
                                                    {...register("degree")}
                                                />
                                            </div>

                                            {/* hospital*/}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Hospital <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"

                                                    className="form-control"
                                                    placeholder="Enter hospital"
                                                    {...register("hospital", {
                                                        required: "Hospital is required",

                                                    })}
                                                />
                                                {errors.hospital && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.hospital.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Commision (%)<span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Enter commision"
                                                    {...register("commision", {
                                                        required: "Commision is required",

                                                    })}
                                                />
                                                {errors.commision && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.commision.message}</p>
                                                )}
                                            </div>
                                            <div className="col-md-8">
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">
                                                        Address <span className="tw-text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        rows="1"
                                                        className="form-control"
                                                        placeholder="Enter full address"
                                                        {...register("address", { required: "Address is required" })}
                                                    />
                                                    {errors.address && (
                                                        <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.address.message}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border rounded p-3">
                                            <label className="tw-block tw-font-medium tw-mb-2">Signature</label>
                                            <div className="d-flex tw-space-x-4 mb-3">
                                                <div className="radio radio-primary">
                                                    <Input
                                                        id="edit-doctor-signature-draw"
                                                        type="radio"
                                                        name="editDoctorSignatureMode"
                                                        checked={signatureMode === "draw"}
                                                        onChange={() => handleSignatureMode("draw")}
                                                    />
                                                    <Label for="edit-doctor-signature-draw" className="mb-0 pe-2">Manual</Label>
                                                </div>
                                                <div className="radio radio-primary">
                                                    <Input
                                                        id="edit-doctor-signature-upload"
                                                        type="radio"
                                                        name="editDoctorSignatureMode"
                                                        checked={signatureMode === "upload"}
                                                        onChange={() => handleSignatureMode("upload")}
                                                    />
                                                    <Label for="edit-doctor-signature-upload" className="mb-0 pe-2">Upload Image</Label>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    {signatureMode === "draw" ? (
                                                        <div className="border rounded p-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <p className="fw-bold mb-0">Draw Signature</p>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary px-2 py-0"
                                                                    onClick={clearSignature}
                                                                >
                                                                    Clear
                                                                </button>
                                                            </div>
                                                            <SignatureCanvas
                                                                ref={signRef}
                                                                penColor="black"
                                                                onEnd={updateCanvasPreview}
                                                                canvasProps={{
                                                                    className: "border w-100 rounded",
                                                                    style: { height: "200px" },
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="border rounded p-3 text-center">
                                                            <p className="fw-bold mb-2">Upload Signature Image</p>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                ref={signatureFileRef}
                                                                style={{ display: "none" }}
                                                                onChange={handleSignatureUpload}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={() => signatureFileRef.current?.click()}
                                                            >
                                                                Upload
                                                            </button>
                                                            {signatureFile ? (
                                                                <p className="text-muted small mt-2 mb-0">{signatureFile.name}</p>
                                                            ) : null}
                                                        </div>
                                                    )}

                                                    <div className="mt-3">
                                                        <label className="tw-block tw-font-medium tw-mb-1">
                                                            Signature Name On Lab Report
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter signature name"
                                                            value={signatureName}
                                                            onChange={(e) => setSignatureName(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="tw-block tw-font-medium tw-mb-1">
                                                            Degree On Lab Report
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter degree name"
                                                            {...register("degree")}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="border rounded p-3 text-center h-100">
                                                        <h5 className="fw-bold mb-3">Preview</h5>
                                                        <SignaturePreview src={signaturePreview} alt="Signature preview" />
                                                        <p className="fw-bold mt-3 mb-0">
                                                            {signatureName || "No Name Entered"}
                                                        </p>
                                                        {reportDegree ? (
                                                            <p className="mb-0">{reportDegree}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="text-end tw-mt-6">
                                            <button
                                                type="submit"
                                                // disabled={isSubmitting}
                                                className="btn btn-primary me-3"
                                            >
                                                {isSubmitting ? "Submitting..." : "Update Doctor"}
                                            </button>
                                            <Link to={`${process.env.PUBLIC_URL}/doctors-list`}>
                                                <button
                                                    className="btn btn-secondary"
                                                >
                                                    Cancel
                                                </button>
                                            </Link>

                                        </div>
                                    </form>
                                </div>
                            </div>

                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
